import Add from '@material-ui/icons/Add'
import lodash from 'lodash'
import { Button, styled } from 'opensolar-ui'
import { useTranslate } from 'ra-core'
import { Fragment, ReactNode, useEffect, useMemo } from 'react'
import { useForm, useFormState } from 'react-final-form'

interface ItemWithId {
  id: string
}

type MultipleCardFieldProps<T> = {
  source: string
  render: (
    item: T & ItemWithId,
    opts: {
      index: number
      deleteCardByID: (id: string, assetKey?: string) => void
    }
  ) => ReactNode
  cardTitle?: string
  addLimit?: number
  assetKey?: string
}
export const BtnWrapper = styled('div', { name: 'BtnWrapper' })({
  justifyContent: 'flex-start',
})

const generateItemId = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  // Fallback for environments without crypto.randomUUID
  return `${Date.now()}-${Math.random().toString(36)}`
}

const MultipleCardField = <T,>({
  source,
  render,
  cardTitle,
  addLimit,
  assetKey
}: MultipleCardFieldProps<T>) => {
  const form = useForm()
  const translate = useTranslate()
  const formValues = useFormState().values
  const items = useMemo(() => lodash.get(formValues, source) || [], [source, formValues])

  // Generate IDs for items that don't have them
  useEffect(() => {
    if (!source || !items.length) return

    const itemsNeedingIds = items.filter((item: T & { id?: string }) => !item.id)
    if (itemsNeedingIds.length > 0) {
      const updatedItems = items.map((item: T & { id?: string }) => {
        if (!item.id) {
          return { ...item, id: generateItemId() }
        }
        return item
      })
      form.change(source, updatedItems)
    }
  }, [source, items.length, form])

  const addCard = () => {
    if (!source)
      return

    const newId = generateItemId()
    const newItem = { id: newId }

    if (assetKey) {
      const assets = formValues.contents?.assets || {}
      const assetField = assets[assetKey] || {}
      const updatedAssets = {
        ...assetField,
        [newId]: {},
      }

      form.change('contents.assets', {
        ...assets,
        [assetKey]: updatedAssets,
      })
    }

    form.change(source, [...items, newItem])
  }

  const deleteCardByID = (id: string, assetKey: string | undefined = undefined) => {
    const itemIndex = items.findIndex((item: T & ItemWithId) => item.id === id)
    if (itemIndex === -1) return

    // Clear any assets by ID before modifying the cards array
    if (assetKey && id) {
      const assets = formValues.contents?.assets || {}
      const assetField = assets[assetKey]

      if (assetField && typeof assetField === 'object' && !Array.isArray(assetField) && assetField[id]) {
        const updatedAssets = { ...assetField }
        delete updatedAssets[id]
        form.change(`contents.assets.${assetKey}`, updatedAssets)
      }
    }

    let updatedCards = [...items]
    updatedCards.splice(itemIndex, 1)
    if (source) form.change(source, updatedCards)
  }

  return (
    <>
      {!!items.length &&
        items.map((item: T & ItemWithId, index) => (
          <Fragment key={item.id}>
            {render(item, {
              index,
              deleteCardByID,
            })}
          </Fragment>
        ))}
      {(addLimit === undefined || addLimit !== items.length) && (
        <BtnWrapper>
          <Button startIcon={<Add />} variant="contained" onClick={addCard}>
            {cardTitle || translate('Card')}
          </Button>
        </BtnWrapper>
      )}
    </>
  )
}

export default MultipleCardField
